<!-- Chat form presenting mutually exclusive options as selectable buttons. -->

<script lang="ts" setup>
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';

import type { SingleSelectInputConfig, SingleSelectInputValue } from '@sigma/common/forms/SingleSelectInput.js';

import Button from '../../Button.vue';

interface Props {
  config: SingleSelectInputConfig;
}

interface Emits {
  submit: [value: SingleSelectInputValue];
}

defineProps<Props>();
defineEmits<Emits>();

const emitAsync = useEmitAsync<Emits>();
</script>

<template>
  <Form
    v-slot="{ submit }"
    :class="css.optionsContainer"
  >
    <Button
      v-for="(option, index) in config.options"
      :key="index"
      :tid="'option-' + index"
      align="left"
      :class="css.optionButton"
      :content-class="css.optionInner"
      @click="submit(() => emitAsync('submit', { type: 'SINGLE_SELECT', option: index, label: option.label }))"
    >
      {{ option.label }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.optionsContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.optionButton {
  padding: $spacing-12;
  border-radius: $border-radius-06;
  border: 1px solid $color-doctor-100;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out;

  &:hover {
    border-color: $color-doctor-200;
    background-color: $color-doctor-50;
  }

  &:focus-visible {
    outline: 2px solid $color-doctor-400;
    outline-offset: 2px;
  }
}

.optionInner {
  justify-content: flex-start;
}
</style>
